import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Kanban, ListChecks, Plus, Sparkles, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";
import { useAuth } from "@/context/AuthContext";
import { canManageTaskDeletion } from "@/lib/taskPermissions";

const STATUSES = ["not_started", "in_progress", "waiting", "completed", "verified"];
const STATUS_LABEL = { not_started: "To Do", in_progress: "In Progress", waiting: "Waiting", completed: "Completed", verified: "Verified", cancelled: "Cancelled" };
const PRIORITIES = ["low", "medium", "high", "urgent"];
const CATEGORIES = [
    { value: "pig", color: "#F4A6A6" }, { value: "poultry", color: "#F4D35E" }, { value: "goat", color: "#B5A28C" },
    { value: "cattle", color: "#A95C3E" }, { value: "duck", color: "#8ECAE6" }, { value: "fish", color: "#219EBC" },
    { value: "crops", color: "#6A994E" }, { value: "inventory", color: "#F2C078" }, { value: "vet", color: "#E63946" },
    { value: "staff", color: "#457B9D" }, { value: "finance", color: "#2D6A4F" }, { value: "maintenance", color: "#6C757D" },
    { value: "deliveries", color: "#F77F00" },
];
const CAT_COLOR = Object.fromEntries(CATEGORIES.map(c => [c.value, c.color]));
const PRIORITY_STYLES = {
    low: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
    medium: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
    high: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
    urgent: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
};
const AI_ENABLED = process.env.REACT_APP_AI_ENABLED === "1";

function TaskCard({ t, employees, onEdit, onDelete, onStatusChange, canDelete }) {
    const emp = (t.assignee_ids || []).map(id => employees.find(e => e.id === id)?.name).filter(Boolean).join(", ");
    const pct = Number(t.completion_pct ?? t.progress ?? 0) || 0;
    return (
        <Card className="p-4 hover:shadow-md transition-shadow cursor-move" data-testid={`task-${t.id}`}>
            <div className="flex items-start gap-2 mb-2">
                <div className="w-1 h-full rounded-full self-stretch" style={{ backgroundColor: CAT_COLOR[t.category] || "#999", minHeight: "40px" }} />
                <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.title}</div>
                    {t.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{t.description}</div>}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-md ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</span>
                <span className="text-xs px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: CAT_COLOR[t.category] || "#999" }}>{t.category}</span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground">{t.area || "No area"}</span>
                {t.due_date && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{t.due_date}</span>}
            </div>
            {emp && <div className="text-xs text-muted-foreground mt-2">👤 {emp}</div>}
            {(t.checklist || []).length > 0 && (
                <div className="text-xs mt-2 text-muted-foreground">✓ {t.checklist.filter(c=>c.done).length}/{t.checklist.length}</div>
            )}
            <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    <span>{i18n.t("batch4.1b90271d66cf")}</span>
                    <span>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
                </div>
            </div>
            <div className="flex gap-1 mt-3">
                <Select value={t.status} onValueChange={(v) => onStatusChange(t, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`status-select-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(t)} data-testid={`edit-task-${t.id}`}>{i18n.t("batch4.5301648dcf6b")}</Button>
                {canDelete && <Button size="sm" variant="destructive" className="h-8" onClick={() => onDelete(t)} data-testid={`delete-task-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>}
            </div>
        </Card>
    );
}

export default function TasksPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canDeleteTasks = canManageTaskDeletion(user);
    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [filter, setFilter] = useState({ priority: "all", category: "all" });
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        const [t, e] = await Promise.all([
            api.get("/tasks").then(r => r.data),
            api.get("/tasks/assignees").then(r => r.data),
        ]);
        setTasks(t); setEmployees(e);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        const body = {
            ...dlg,
            estimated_minutes: Number(dlg.estimated_minutes) || 0,
            checklist: (dlg.checklist_str || "").split("\n").filter(l => l.trim()).map(l => ({ text: l.trim(), done: false })),
        };
        delete body.checklist_str;
        try {
            if (dlg.id) await api.put(`/tasks/${dlg.id}`, body);
            else await api.post("/tasks", body);
            toast.success("Task saved"); setDlg(null); await load();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Could not save task");
        } finally {
            setSaving(false);
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/tasks/${deleteTarget.id}`);
            setTasks(current => current.filter(task => task.id !== deleteTarget.id));
            setDeleteTarget(null);
            window.dispatchEvent(new CustomEvent("ajfarm:tasks-changed", { detail: { action: "delete" } }));
            toast.success("Task deleted");
            await load();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Could not delete task");
        } finally {
            setDeleting(false);
        }
    };
    const changeStatus = async (t, status) => {
        await api.put(`/tasks/${t.id}`, { ...t, status, completion_pct: status === "completed" || status === "verified" ? 100 : (t.completion_pct || 0) });
        load();
    };

    const suggestAI = () => navigate("/ai-katulong?capability=task_priority");

    const filtered = tasks.filter(t => {
        if (filter.priority !== "all" && t.priority !== filter.priority) return false;
        if (filter.category !== "all" && t.category !== filter.category) return false;
        return true;
    });

    const byStatus = STATUSES.map(s => ({ status: s, tasks: filtered.filter(t => (t.status || "not_started") === s) }));

    const openNew = () => setDlg({
        title: "", description: "", area: "", priority: "medium", category: "pig",
        assignee_ids: [], due_date: farmDateIso(),
        estimated_minutes: 60, checklist_str: "", status: "not_started", completion_pct: 0,
        source_submission_id: globalThis.crypto?.randomUUID?.() || `task-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    });

    return (
        <div className="space-y-6" data-testid="tasks-page">
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This task will be removed from active task lists, counts, alerts, and calendars. Its audit history will be preserved.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={deleting}
                            onClick={(event) => { event.preventDefault(); confirmDelete(); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-task"
                        >{deleting ? "Deleting…" : "Delete Task"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Kanban className="w-8 h-8 text-primary" />
                    <div>
                        <div className="tiny-label">{i18n.t("batch4.a1fdaa6b2a84")}</div>
                        <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch4.090ec5f560fc")}</h1>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {AI_ENABLED && <Button variant="outline" className="h-12" onClick={suggestAI} data-testid="ai-suggest-btn">
                        <Sparkles className="w-4 h-4 mr-2" /> {i18n.t("batch4.159edd2246da")}
                    </Button>}
                    <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                        <DialogTrigger asChild>
                            <Button className="h-12" onClick={openNew} data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> {i18n.t("batch4.cc3dbd47e1cf")}</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.7bb0ddf9221c")}</DialogTitle></DialogHeader>
                            {dlg && <div className="space-y-4">
                                <div><Label className="tiny-label">{i18n.t("batch4.9616975c7a0e")}</Label><Input value={dlg.title} onChange={e => setDlg({...dlg, title: e.target.value})} className="h-12 mt-2" data-testid="task-title-input"/></div>
                                <div><Label className="tiny-label">{i18n.t("batch4.55f8ebc805e6")}</Label><Textarea value={dlg.description} onChange={e => setDlg({...dlg, description: e.target.value})} rows={3}/></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="tiny-label">{i18n.t("batch4.a3c686e711e4")}</Label>
                                        <Select value={dlg.category} onValueChange={v => setDlg({...dlg, category: v})}>
                                            <SelectTrigger className="h-12 mt-2" data-testid="task-category-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="tiny-label">{i18n.t("batch4.886cbff9d9df")}</Label>
                                        <Select value={dlg.priority} onValueChange={v => setDlg({...dlg, priority: v})}>
                                            <SelectTrigger className="h-12 mt-2" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="tiny-label">{i18n.t("batch4.a1b308ec704a")}</Label><Input type="date" value={dlg.due_date} onChange={e => setDlg({...dlg, due_date: e.target.value})} className="h-12 mt-2"/></div>
                                    <div><Label className="tiny-label">{i18n.t("batch4.4f30761a385b")}</Label><Input type="number" value={dlg.estimated_minutes} onChange={e => setDlg({...dlg, estimated_minutes: e.target.value})} className="h-12 mt-2 font-mono"/></div>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch4.2745debaa64a")}</Label><Input value={dlg.area || ""} onChange={e => setDlg({...dlg, area: e.target.value})} className="h-12 mt-2" placeholder={i18n.t("batch4.488ecae3a1c3")} data-testid="task-area-input"/></div>
                                <div><Label className="tiny-label">{i18n.t("batch4.049e3ce5476a")}</Label>
                                    <Select value={(dlg.assignee_ids || [])[0] || ""} onValueChange={v => setDlg({...dlg, assignee_ids: v ? [v] : []})}>
                                        <SelectTrigger className="h-12 mt-2" data-testid="task-assignee-select"><SelectValue placeholder={i18n.t("batch4.7776e6a0ff82")} /></SelectTrigger>
                                        <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch4.c1481b778dcf")}</Label><Textarea value={dlg.checklist_str} onChange={e => setDlg({...dlg, checklist_str: e.target.value})} rows={4} placeholder={i18n.t("batch4.c7b3d4127c30")}/></div>
                                <Button onClick={save} disabled={saving} className="h-12 w-full" data-testid="save-task-btn">{saving ? "Saving…" : "Save Task"}</Button>
                            </div>}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="flex gap-3 flex-wrap">
                <Select value={filter.priority} onValueChange={v => setFilter({...filter, priority: v})}>
                    <SelectTrigger className="h-11 w-40" data-testid="filter-priority"><SelectValue placeholder={i18n.t("batch4.886cbff9d9df")} /></SelectTrigger>
                    <SelectContent><SelectItem value="all">{i18n.t("batch4.bc6ba0d1bbf5")}</SelectItem>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filter.category} onValueChange={v => setFilter({...filter, category: v})}>
                    <SelectTrigger className="h-11 w-40" data-testid="filter-category"><SelectValue placeholder={i18n.t("batch4.a3c686e711e4")} /></SelectTrigger>
                    <SelectContent><SelectItem value="all">{i18n.t("batch4.060be00f4f26")}</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>)}</SelectContent>
                </Select>
            </div>

            <Tabs defaultValue="kanban">
                <TabsList>
                    <TabsTrigger value="kanban" data-testid="tab-kanban"><Kanban className="w-4 h-4 mr-1" />{i18n.t("batch4.389554673091")}</TabsTrigger>
                    <TabsTrigger value="list" data-testid="tab-list"><ListChecks className="w-4 h-4 mr-1" />{i18n.t("batch4.a1fffaaafb7c")}</TabsTrigger>
                </TabsList>
                <TabsContent value="kanban" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {byStatus.map(col => (
                            <div key={col.status} className="space-y-2" data-testid={`kanban-col-${col.status}`}>
                                <div className="flex items-center justify-between px-2">
                                    <div className="tiny-label">{STATUS_LABEL[col.status]}</div>
                                    <span className="text-xs font-mono font-bold bg-secondary text-secondary-foreground px-2 py-0.5 rounded">{col.tasks.length}</span>
                                </div>
                                <div className="space-y-2 min-h-[200px] p-2 bg-secondary/30 rounded-xl">
                                    {col.tasks.map(t => <TaskCard key={t.id} t={t} employees={employees} onEdit={(t)=>setDlg({...t, checklist_str: (t.checklist||[]).map(c=>c.text).join("\n")})} onDelete={setDeleteTarget} onStatusChange={changeStatus} canDelete={canDeleteTasks}/>)}
                                    {col.tasks.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">{i18n.t("batch4.e542695cd353")}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="list" className="mt-6">
                    <div className="space-y-2">
                        {filtered.map(t => (
                            <Card key={t.id} className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: CAT_COLOR[t.category] }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">{t.title}</div>
                                        <div className="text-xs text-muted-foreground">{t.category} · {t.priority} · {t.due_date} · {STATUS_LABEL[t.status]}</div>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => setDlg({...t, checklist_str: (t.checklist||[]).map(c=>c.text).join("\n")})}>{i18n.t("batch4.5301648dcf6b")}</Button>
                                    {canDeleteTasks && <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(t)} data-testid={`delete-task-list-${t.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                    </Button>}
                                </div>
                            </Card>
                        ))}
                        {filtered.length === 0 && <Card className="p-8 text-center text-muted-foreground">{i18n.t("batch4.c1c785eaec5f")}</Card>}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
